import { useMemo } from "react";
import { StackChart } from "@/charts/StackChart";
import { ChartBox, ChartInfo } from "@/components/ChartInfo";
import { type HeadCard, Headline } from "@/components/Headline";
import { EntityKind } from "@/generated/core/v1/financial_plan";
import { formatMonthShort } from "@/plan-logic/lib/dates";
import { money, moneyShort } from "@/plan-logic/lib/format";
import {
  CASH_KINDS,
  firstState,
  investedByAccount,
  investedTotal,
  KIND_LABEL,
  PROPERTY_KIND_NAME,
  retirementMarker,
  stateAt,
} from "@/plan-logic/plan-view";
import { ConnectEmpty } from "../ConnectEmpty";
import { PlanPage, type PlanCtx } from "../PlanPage";

const PALETTE = [
  "var(--ess)",
  "var(--save)",
  "var(--life)",
  "var(--debt)",
  "var(--s5)",
  "var(--s6)",
  "var(--warn)",
];

const INVEST_SUB =
  "This view is built from your investment accounts. Investment connections are coming soon; until then your plan uses the balances you typed in.";

/**
 * Prototype v44's Assets page, filled from the plan and the family: what you hold today, every
 * invested account growing on Abi's plan, each account's balance, and property. What only
 * Wealthica can supply (each account's return, asset classes, holdings, the last twelve months)
 * shows as not connected yet; nothing is invented for it.
 */
function Body({ ctx }: { ctx: PlanCtx }) {
  const { abi, naive, family, index } = ctx;
  const s0 = firstState(abi);
  const ret = retirementMarker(abi, family);
  const growth = useMemo(() => investedByAccount(abi, ret?.date), [abi, ret?.date]);

  const invested = investedTotal(s0);
  const cash = (s0?.accounts ?? [])
    .filter((a) => CASH_KINDS.has(a.kind))
    .reduce((t, a) => t + a.balance, 0);
  const property = (s0?.assets ?? [])
    .filter((a) => a.kind === EntityKind.PROPERTY)
    .reduce((t, a) => t + a.balance, 0);

  const head: HeadCard[] = [
    {
      k: "What you hold",
      v: money(invested + cash + property),
      s: `${money(invested)} invested, ${money(cash)} in cash, ${money(property)} in property`,
      testId: "ast-held",
    },
    {
      k: "Last 12 months",
      v: "Waiting on your accounts",
      pending: true,
      s: "What these accounts did over the past year needs their history, which comes with connecting your investments.",
    },
  ];
  if (ret) {
    const at = (p: typeof abi | undefined) => investedTotal(stateAt(p, ret.date));
    const a = at(abi);
    if (naive) {
      const n = at(naive);
      const d = a - n;
      head.push({
        k: "With Abi’s plan",
        abi: true,
        from: moneyShort(n),
        to: `${moneyShort(a)} invested`,
        lead: `${d >= 0 ? "+" : "−"}${moneyShort(Math.abs(d))} invested`,
        leadTone: Math.round(d) > 0 ? "ok" : Math.round(d) < 0 ? "debt" : "flat",
        s: `by ${ret.year}, the year you stop working`,
        testId: "hd-abi",
      });
    } else {
      head.push({
        k: "With Abi’s plan",
        abi: true,
        v: `${moneyShort(a)} invested`,
        s: `by ${ret.year}, the year you stop working`,
      });
    }
  }

  const name = (uuid: string, kind: EntityKind) => index.name(uuid, KIND_LABEL[kind]);
  const series = growth.uuids.map((u, i) => ({
    k: u,
    n: name(u, growth.kinds.get(u) ?? EntityKind.UNSPECIFIED),
    c: PALETTE[i % PALETTE.length],
  }));
  const colour = new Map(series.map((s) => [s.k, s.c]));
  const rate = abi.meta?.assumptions?.investmentReturnRate;

  const accounts = (s0?.accounts ?? []).filter((a) => a.balance > 0.5 || CASH_KINDS.has(a.kind));
  const ownerOf = (uuid: string) => {
    const m = index.get(uuid)?.memberUuid;
    return m ? index.person(m) : "";
  };

  const homes = family.properties.map((p) => {
    const value = s0?.assets.find((a) => a.entityUuid === p.uuid)?.balance ?? p.currentValue ?? 0;
    const owing = family.mortgages
      .filter((m) => m.propertyUuid === p.uuid)
      .reduce((t, m) => t + (s0?.debts.find((d) => d.entityUuid === m.uuid)?.balance ?? 0), 0);
    return { p, value, owing };
  });

  return (
    <>
      <Headline id="hd-ast" cards={head} />

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="cf-bar" style={{ marginBottom: 14 }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>What you have invested, year by year</h2>
            <p className="mut" style={{ margin: 0, maxWidth: "64ch" }}>
              Every account stacked, growing on Abi&rsquo;s plan.
              {property > 0.5 && (
                <>
                  {" "}
                  Your home is not in here &mdash; at {money(property)} it would flatten everything
                  else, and it is in its own section below.
                </>
              )}
            </p>
          </div>
        </div>
        <ChartBox id="ast-growth">
          {series.length ? (
            <StackChart
              rows={growth.rows.map((r, i) => ({ label: i ? r.year : "Now", values: r.values }))}
              series={series}
              barWidth={16}
              height={300}
              colLabel="Invested"
              label={`Investments by account${ret ? ` to ${ret.year}` : ""}, growing on Abi's plan`}
              note={`From Abi’s plan: what each account holds at the end of each year, with what the plan puts in${
                rate
                  ? ` and growth at the ${+rate.toFixed(2)}% a year the plan is built on`
                  : " and what it earns"
              }. Projections are not guarantees.`}
              testId="ast-growth-chart"
            />
          ) : (
            <p className="mut">Nothing invested yet, and the plan doesn&rsquo;t open an account.</p>
          )}
        </ChartBox>
      </div>

      <div className="grid2" style={{ marginBottom: 16 }}>
        <div className="card pad">
          <h2 style={{ marginBottom: 14 }}>
            Each account, and what it has returned
            <ChartInfo k="ast-accts" inline />
          </h2>
          <div id="ast-accts" data-testid="ast-accts">
            {accounts.map((a) => {
              const isCash = CASH_KINDS.has(a.kind);
              const owner = ownerOf(a.entityUuid);
              return (
                <div className="astrow" key={a.entityUuid}>
                  <span
                    className="sw"
                    style={{ background: colour.get(a.entityUuid) ?? "var(--ink3)" }}
                  />
                  <span className="nm">
                    {name(a.entityUuid, a.kind)}
                    <span className="sub">
                      {KIND_LABEL[a.kind]}
                      {owner ? ` · ${owner}` : ""}
                      {isCash ? " · not invested" : ""}
                    </span>
                  </span>
                  <span className="vl tnum">{money(a.balance)}</span>
                  <span className="gn flat">&mdash;</span>
                </div>
              );
            })}
            <p className="hint">
              What each account has returned, its value against everything ever put into it, comes
              with connecting your investments. It is not an annual return: an account opened last
              year and one opened ten years ago are not on the same clock.
            </p>
          </div>
        </div>
        <div className="card pad">
          <h2 style={{ marginBottom: 14 }}>
            What it is invested in
            <ChartInfo k="ast-class" inline />
          </h2>
          <div id="ast-class">
            <ConnectEmpty
              what="What your money is invested in, by asset class, comes from each fund’s own mandate."
              sub={INVEST_SUB}
            />
          </div>
        </div>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>
          Everything you hold
          <ChartInfo k="ast-hold" inline />
        </h2>
        <p className="mut" style={{ marginBottom: 16 }}>
          What you paid, what it is worth, and the difference.
        </p>
        <div id="ast-hold">
          <ConnectEmpty
            what="Every holding, with its units, price, what you paid, and what it is worth now."
            sub={INVEST_SUB}
          />
        </div>
      </div>

      <div className="card pad">
        <h2 style={{ marginBottom: 4 }}>
          Property
          <ChartInfo k="ast-prop" inline />
        </h2>
        <p className="mut" style={{ marginBottom: 16, maxWidth: "68ch" }}>
          Down here because it barely moves. A house is revalued once a year at best, so a chart of
          it is a flat line with a step in it.
        </p>
        <div id="ast-prop" data-testid="ast-prop">
          {homes.length === 0 && <p className="mut">No property in your plan.</p>}
          {homes.map(({ p, value, owing }) => {
            const g = p.purchasePrice ? value - p.purchasePrice : undefined;
            const bought = p.purchaseDate?.year ? `Bought in ${p.purchaseDate.year}. ` : "";
            return (
              <div className="astrow prop" key={p.uuid}>
                <span className="sw" style={{ background: "var(--before)" }} />
                <span className="nm">
                  {index.name(p.uuid, PROPERTY_KIND_NAME[p.kind])}
                  <span className="sub">
                    {bought}
                    {owing > 0.5
                      ? `${money(owing)} of mortgage against it; ${money(value - owing)} of it is yours.`
                      : "Nothing owing against it."}
                  </span>
                </span>
                <span className="vl tnum">{money(value)}</span>
                <span className={`gn ${g === undefined ? "flat" : g >= 0 ? "up" : "dn"}`}>
                  {g === undefined
                    ? "—"
                    : `${g >= 0 ? "+" : "−"}${money(Math.abs(g))}${
                        p.purchasePrice ? ` (${Math.round((g / p.purchasePrice) * 100)}%)` : ""
                      }`}
                </span>
              </div>
            );
          })}
          {homes.length > 0 && (
            <p className="hint">
              At its value in your plan as of {formatMonthShort(ctx.planStart)}
              {homes.some((h) => h.p.purchasePrice) ? ", against what you paid for it" : ""}.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

/** Prototype v44's Assets page. */
export function AssetsPage() {
  return (
    <PlanPage title="pages.assets.title" sub="pages.assets.sub" testId="assets">
      {(ctx) => <Body ctx={ctx} />}
    </PlanPage>
  );
}
